<template>
  <div class="breakeven-analysis-container">
    <!-- 盈亏平衡分析表 -->
    <div class="table-wrapper">
      <div class="table-scroll-area">
        <table class="breakeven-table">
          <thead>
            <tr>
              <th class="seq-col">计算项目</th>
              <th class="year-col">计算期</th>
              <th
                v-for="index in localBreakevenData.calculationPeriod"
                :key="'year-' + index"
              >
                {{ index }}
              </th>
            </tr>
          </thead>
          <tbody>
            <!-- 年销售收入 -->
            <tr>
              <td class="item-col">年销售收入</td>
              <td class="unit-col">万元</td>
              <td
                v-for="index in localBreakevenData.calculationPeriod"
                :key="'revenue-' + index"
                class="year-data-col"
              >
                {{
                  formatNumber(
                    (localBreakevenData.annualRevenueByYear || [])[index - 1] ||
                      0
                  )
                }}
              </td>
            </tr>

            <!-- 年销售税金及附加 -->
            <tr>
              <td class="item-col">年销售税金及附加</td>
              <td class="unit-col">万元</td>
              <td
                v-for="index in localBreakevenData.calculationPeriod"
                :key="'sales-tax-' + index"
                class="year-data-col"
              >
                {{
                  formatNumber(
                    (localBreakevenData.salesTaxByYear || [])[index - 1] || 0
                  )
                }}
              </td>
            </tr>

            <!-- 年增值税 -->
            <tr>
              <td class="item-col">年增值税</td>
              <td class="unit-col">万元</td>
              <td
                v-for="index in localBreakevenData.calculationPeriod"
                :key="'vat-' + index"
                class="year-data-col"
              >
                {{
                  formatNumber(
                    (localBreakevenData.vatByYear || [])[index - 1] || 0
                  )
                }}
              </td>
            </tr>

            <!-- 年可变成本 -->
            <tr>
              <td class="item-col">年可变成本</td>
              <td class="unit-col">万元</td>
              <td
                v-for="index in localBreakevenData.calculationPeriod"
                :key="'variable-cost-' + index"
                class="year-data-col"
              >
                {{
                  formatNumber(
                    (localBreakevenData.variableCostByYear || [])[index - 1] ||
                      0
                  )
                }}
              </td>
            </tr>

            <!-- 年固定总成本 -->
            <tr>
              <td class="item-col">年固定总成本</td>
              <td class="unit-col">万元</td>
              <td
                v-for="index in localBreakevenData.calculationPeriod"
                :key="'fixed-cost-' + index"
                class="year-data-col"
              >
                {{
                  formatNumber(
                    (localBreakevenData.fixedCostByYear || [])[index - 1] || 0
                  )
                }}
              </td>
            </tr>

            <!-- BEP(%)，生产能力利用率 -->
            <tr>
              <td class="item-col">BEP(%)，生产能力利用率</td>
              <td class="unit-col">%</td>
              <td
                v-for="index in localBreakevenData.calculationPeriod"
                :key="'bep-' + index"
                class="year-data-col"
              >
                {{
                  formatPercent(
                    (localBreakevenData.bepUtilizationRateByYear || [])[
                      index - 1
                    ] || 0
                  )
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 盈亏平衡指标 -->
    <div class="indicators-section">
      <div class="indicator-item">
        <div class="indicator-label">盈亏平衡收入</div>
        <div class="indicator-unit">万元/年</div>
        <div class="indicator-value">
          {{ formatNumber(breakevenRevenue, 2) }}
        </div>
      </div>
      <div class="indicator-item">
        <div class="indicator-label">盈亏平衡产能利用率</div>
        <div class="indicator-unit">%</div>
        <div class="indicator-value">
          {{ formatPercent(breakevenUtilizationRate, 2) }}
        </div>
      </div>
    </div>

    <!-- 盈亏平衡分析图 -->
    <div class="chart-section">
      <div class="chart-container">
        <div id="breakeven-chart" style="width: 100%; height: 100%"></div>
      </div>
      <div class="chart-legend-container">
        <h4
          style="
            margin-top: 0;
            margin-bottom: 10px;
            font-size: 14px;
            font-weight: 600;
            text-align: center;
          "
        >
          图例
        </h4>
        <div
          style="
            font-size: 11px;
            color: #666;
            text-align: center;
            margin-bottom: 15px;
            padding: 4px;
            background: #f5f7fa;
            border-radius: 3px;
          "
        >
          金额单位：万元
        </div>
        <div class="chart-legend-item">
        <div class="legend-color" style="background-color: #409eff"></div>
        <span class="legend-text">营业收入（不含税）</span>
      </div>
      <div class="chart-legend-item">
        <div class="legend-color" style="background-color: #f56c6c"></div>
        <span class="legend-text">可变成本</span>
      </div>
      <div class="chart-legend-item">
        <div class="legend-color" style="background-color: #909399"></div>
        <span class="legend-text">固定成本</span>
      </div>
      <div class="chart-legend-item">
        <div class="legend-color" style="background-color: #E6A23C"></div>
        <span class="legend-text">{{ bepLabel }}</span>
      </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { breakevenAnalysisCalculation } from "@/utils/comp.js";
import * as echarts from "echarts";

export default {
  name: "BreakevenAnalysis",
  data() {
    return {
      breakevenChart: null,
      projectData: {},
      localBreakevenData: {
        annualRevenueByYear: [],
        salesTaxByYear: [],
        vatByYear: [],
        variableCostByYear: [],
        fixedCostByYear: [],
        bepUtilizationRateByYear: [],
        calculationPeriod: 15,
      },
      breakevenRevenue: 0,
      breakevenUtilizationRate: 0,
      normalYearIndex: 0,
      _resizeHandler: null,
      bepLabel: "BEP(%)", // 盈亏平衡标签，用于图表和图例
    };
  },

  created() {
    console.log("=== 盈亏平衡分析组件创建 ===");

    // 直接从VUEX获取项目数据
    const storeProjectData = this.$store.state.projectData;

    if (!storeProjectData) {
      console.warn("项目数据为空");
      return;
    }

    // 设置本地项目数据
    this.projectData = storeProjectData;

    // 获取盈亏平衡分析数据
    const bepData = breakevenAnalysisCalculation();
    // 获取计算期
    const calculationPeriod = bepData.calculationPeriod || 15;

    // 计算正常年索引
    const normalYear = storeProjectData?.基础数据?.normalYear || 0;
    this.normalYearIndex = Math.max(0, normalYear - 1);

    // 设置本地数据
    this.localBreakevenData = bepData;

    // 计算盈亏平衡指标
    this.calculateBreakevenIndicators();
  },

  mounted() {
    console.log("组件挂载，开始绘制图表...");
    // 使用nextTick确保DOM已渲染
    this.$nextTick(() => {
      this.drawBreakevenChart();
    });
  },

  beforeDestroy() {
    // 清理图表资源
    if (this.breakevenChart) {
      this.breakevenChart.dispose();
      this.breakevenChart = null;
    }
    // 清理resize监听器
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler);
      this._resizeHandler = null;
    }
  },

  methods: {
    // 计算盈亏平衡指标
    calculateBreakevenIndicators() {
      // 计算盈亏平衡指标
      const fixedCost =
        this.localBreakevenData.fixedCostByYear[this.normalYearIndex] || 0;
      const revenue =
        this.localBreakevenData.annualRevenueByYear[this.normalYearIndex] || 0;
      const variableCost =
        this.localBreakevenData.variableCostByYear[this.normalYearIndex] || 0;

      // 计算边际贡献率
      const contributionMarginRatio =
        revenue > 0 ? (revenue - variableCost) / revenue : 0;

      // 计算盈亏平衡收入
      this.breakevenRevenue =
        contributionMarginRatio > 0 ? fixedCost / contributionMarginRatio : 0;

      // 计算盈亏平衡产能利用率（基于计算出的盈亏平衡收入）
      this.breakevenUtilizationRate =
        revenue > 0 ? this.breakevenRevenue / revenue : 0;
      
      // 更新BEP标签，显示实际百分比值，保留2位小数
      const bepPercentage = (this.breakevenUtilizationRate * 100).toFixed(2);
      this.bepLabel = `BEP(${bepPercentage}%)`;

      // 保存结果到Vuex
      this.saveBreakevenResult();
    },

    // 保存盈亏平衡结果到Vuex
    saveBreakevenResult() {
      // 获取当前项目数据
      const storeProjectData = this.$store.state.projectData;
      if (!storeProjectData) return;

      // 确保不确定性分析和盈亏平衡分析数据结构存在
      if (!storeProjectData.不确定性分析) {
        storeProjectData.不确定性分析 = {};
      }
      if (!storeProjectData.不确定性分析.盈亏平衡分析) {
        storeProjectData.不确定性分析.盈亏平衡分析 = {};
      }

      // 更新盈亏平衡分析数据
      storeProjectData.不确定性分析.盈亏平衡分析.breakevenRevenue =
        this.breakevenRevenue;
      storeProjectData.不确定性分析.盈亏平衡分析.breakevenUtilizationRate =
        this.breakevenUtilizationRate;
      storeProjectData.不确定性分析.盈亏平衡分析.normalYearIndex =
        this.normalYearIndex;

      // 更新VUEX
      this.$store.commit("SET_PROJECT_DATA", storeProjectData);
    },
    // 格式化数字显示
    formatNumber(num, decimalPlaces = 2) {
      if (num === null || num === undefined || isNaN(num)) {
        return "";
      }

      // 保留指定位数小数
      const formatted = Number(num).toFixed(decimalPlaces);

      // 如果值为0，则显示为空
      if (parseFloat(formatted) === 0) {
        return "";
      }

      // 返回格式化后的数字
      return formatted;
    },

    // 格式化百分比显示
    formatPercent(num, decimalPlaces = 2) {
      if (num === null || num === undefined || isNaN(num)) {
        return "";
      }

      // 检查是否已经是百分比值（大于1）
      let percentValue;
      if (num > 1) {
        // 如果已经是百分比（如50而不是0.5），直接使用
        percentValue = num;
      } else {
        // 将小数转换为百分比（如0.5转为50%）
        percentValue = num * 100;
      }

      // 保留指定位数小数并添加百分号
      const formatted = percentValue.toFixed(decimalPlaces) + "%";

      // 如果值为0%，则显示为空
      if (parseFloat(percentValue.toFixed(decimalPlaces)) === 0) {
        return "";
      }

      // 返回格式化后的百分比
      return formatted;
    },

    // 初始化数组，确保长度正确
    initializeArray(array, length) {
      if (!Array.isArray(array)) {
        array = [];
      }

      const result = [];
      for (let i = 0; i < length; i++) {
        result.push(array[i] || 0);
      }
      return result;
    },

    // 绘制盈亏平衡分析图
    // 绘制盈亏平衡分析图 - 修复Y轴最大值计算
    drawBreakevenChart() {
      console.log("=== 开始绘制盈亏平衡图表 ===");

      // 清理旧图表实例
      if (this.breakevenChart) {
        this.breakevenChart.dispose();
        this.breakevenChart = null;
      }

      // 获取图表容器
      const chartDom = document.getElementById("breakeven-chart");
      if (!chartDom) {
        console.warn("图表容器未找到");
        return;
      }
      console.log("图表容器找到:", chartDom);
      
      // 处理不可见的图表容器
      const originalDisplay = chartDom.style.display;
      const originalWidth = chartDom.style.width;
      const originalHeight = chartDom.style.height;
      
      // 临时设置容器为可见状态，以便ECharts能够正确计算尺寸
      if (originalDisplay === "none" || !chartDom.offsetWidth || !chartDom.offsetHeight) {
        chartDom.style.display = "block";
        chartDom.style.width = "800px";
        chartDom.style.height = "400px";
        console.log("临时设置图表容器为可见状态");
      }

      // 初始化图表
      try {
        this.breakevenChart = echarts.init(chartDom);
        console.log("图表初始化成功");
        
        // 将图表实例保存到全局
        if (!window.chartInstances) {
          window.chartInstances = {};
        }
        window.chartInstances.breakevenChart = this.breakevenChart;
        console.log("图表实例已保存到全局");
        
        // 恢复容器原来的显示状态
        chartDom.style.display = originalDisplay;
        if (originalWidth) chartDom.style.width = originalWidth;
        if (originalHeight) chartDom.style.height = originalHeight;
        console.log("恢复图表容器原来的显示状态");
      } catch (error) {
        console.error("图表初始化失败:", error);
        // 确保无论如何都恢复容器原来的显示状态
        chartDom.style.display = originalDisplay;
        if (originalWidth) chartDom.style.width = originalWidth;
        if (originalHeight) chartDom.style.height = originalHeight;
        return;
      }

      // 获取当前数据（单位：万元）
      const fixedCost =
        this.localBreakevenData.fixedCostByYear[this.normalYearIndex] || 0;
      const revenue =
        this.localBreakevenData.annualRevenueByYear[this.normalYearIndex] || 0;
      const variableCost =
        this.localBreakevenData.variableCostByYear[this.normalYearIndex] || 0;
      const bepRate =
        this.localBreakevenData.bepUtilizationRateByYear[
          this.normalYearIndex
        ] || 0;

      console.log("图表使用的数据（万元）:", {
        固定成本: fixedCost,
        销售收入: revenue,
        可变成本: variableCost,
        盈亏平衡率: bepRate,
        normalYearIndex: this.normalYearIndex,
      });

      // 如果数据为0，显示空图表
      if (revenue <= 0 && variableCost <= 0 && fixedCost <= 0) {
        console.log("数据全为0，显示空图表");
        this.breakevenChart.setOption({
          title: {
            text: "暂无数据",
            left: "center",
            top: "center",
            textStyle: {
              color: "#999",
              fontSize: 14,
            },
          },
        });
        return;
      }
      console.log("数据有效，继续绘制图表");

      // 生产负荷从0%到100%，步长10%
      const loadValues = Array.from({ length: 11 }, (_, i) => i * 10);

      // 计算各负荷下的值（单位：万元）
      const revenueValues = loadValues.map((load) => (load / 100) * revenue);
      const variableCostValues = loadValues.map(
        (load) => (load / 100) * variableCost
      );
      const totalCostValues = variableCostValues.map((vc) => vc + fixedCost);

      // 计算盈亏平衡点
      const breakevenLoad = bepRate * 100;
      // 使用组件中已计算好的盈亏平衡收入
      const breakevenRevenue = this.breakevenRevenue;

      console.log("计算后的曲线数据（万元）:", {
        满负荷收入: revenueValues[10],
        满负荷总成本: totalCostValues[10],
        盈亏平衡点收入: breakevenRevenue,
        固定成本: fixedCost,
      });

      // 使用正常年销售收入*1.2作为Y轴最大值
      const maxYValue = Math.max(revenue * 1.2, 1); // 确保至少为1，避免除以0

      // 准备系列数据
      const series = [
        {
          name: "营业收入（不含税）",
          type: "line",
          data: loadValues.map((load, index) => [load, revenueValues[index]]),
          lineStyle: {
          color: "#409EFF",
          width: 1, // 线条变细
        },
          smooth: false,
          symbol: "none",
        },
        {
          name: "可变成本",
          type: "line",
          data: loadValues.map((load, index) => [
            load,
            variableCostValues[index],
          ]),
          lineStyle: {
          color: "#F56C6C",
          width: 1, // 线条变细
        },
          smooth: false,
          symbol: "none",
        },
        {
          name: "固定成本",
          type: "line",
          data: loadValues.map((load) => [load, fixedCost]),
          lineStyle: {
          color: "#909399",
          width: 1, // 线条变细
          type: "solid",
        },
          smooth: false,
          symbol: "none",
        },
        {
          name: this.bepLabel,
          type: "line",
          data: loadValues.map((load) => [load, breakevenRevenue]),
          lineStyle: {
          color: "#E6A23C",
          width: 1, // 线条变细
          type: "dashed",
          opacity: 0.9,
        },
          smooth: false,
          symbol: "none",
          // 确保线始终显示
          z: 10,
        },
      ];

      // **关键修复：如果数据太小，显示提示信息**
      let chartTitle = "盈亏平衡分析图";
      const maxDataValue = revenue;
      if (maxDataValue < 0.1) {
        chartTitle = "盈亏平衡分析图 (数据过小，已放大显示)";
      }

      // 图表配置
      const option = {
        title: {
          text: chartTitle,
          textStyle: {
            fontSize: 14,
            fontWeight: "bold",
          },
          left: "center",
          top: 10,
        },
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "cross",
            label: {
              backgroundColor: "#6a7985",
              formatter: function (params) {
                if (params.axisDimension === "x") {
                  return `负荷: ${params.value}%`;
                }
                const value = Number(params.value) || 0;
                return `${value.toFixed(2)}`;
              },
            },
          },
          formatter: function (params) {
            let result = `<div style="margin-bottom: 5px; font-weight: bold;">生产负荷: ${params[0].axisValue}%</div>`;
            params.forEach((param) => {
              if (param.seriesName !== "BEP垂直线") {
                const rawValue = param.value[1] || param.value;
                const value = Number(rawValue) || 0;
                const color = param.color || "#666";
                result += `
              <div style="display: flex; align-items: center; margin: 2px 0;">
                <span style="display: inline-block; width: 8px; height: 8px; background: ${color}; margin-right: 5px; border-radius: 50%;"></span>
                <span style="flex: 1;">${param.seriesName}:</span>
                <span style="font-weight: bold; margin-left: 10px;">${value.toFixed(2)}</span>
              </div>
            `;
              }
            });
            return result;
          },
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          borderColor: "#dcdfe6",
          borderWidth: 1,
          padding: [8, 12],
          textStyle: {
            fontSize: 12,
          },
        },
        legend: {
          show: false,
        },
        grid: {
          left: "18%", // 增加左侧空间，确保X轴名称完全显示
          right: "10%",
          bottom: "15%", // 增加底部空间，防止X轴标签溢出
          top: "15%",
          containLabel: true,
        },
        xAxis: {
          type: "value",
          name: "生产负荷 (%)",
          nameTextStyle: {
            fontSize: 11,
            fontWeight: "normal",
            color: "#333",
          },
          nameLocation: "center",
          nameGap: 25,
          axisLabel: {
            fontSize: 11,
            formatter: "{value}%",
            color: "#666",
          },
          min: 0,
          max: 100,
          interval: 20,
          axisLine: {
            show: true,
            lineStyle: {
              color: "#606266", // 加深轴线颜色
              width: 1.5, // 加粗轴线
            },
          },
          axisTick: {
            show: true,
            length: 5,
            lineStyle: {
              color: "#606266", // 加深刻度线颜色
            },
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: "dashed",
              color: "#C0C4CC", // 加深分割线颜色
              opacity: 0.6, // 调整透明度
            },
          },
        },
        yAxis: {
          type: "value",
          name: "",
          nameTextStyle: {
            fontSize: 11,
            fontWeight: "normal",
            color: "#333",
          },
          axisLabel: {
            fontSize: 11,
            formatter: function (value) {
              // **关键：直接显示数值，保留2位小数**
              return value.toFixed(2); // 所有值显示2位小数
            },
            color: "#666",
          },
          min: 0,
          max: maxYValue,
          splitNumber: 5,
          axisLine: {
            show: true,
            lineStyle: {
              color: "#606266", // 加深轴线颜色
              width: 1.5, // 加粗轴线
            },
          },
          axisTick: {
            show: true,
            length: 5,
            lineStyle: {
              color: "#606266", // 加深刻度线颜色
            },
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: "dashed",
              color: "#C0C4CC", // 加深分割线颜色
              opacity: 0.6, // 调整透明度
            },
          },
        },
        series: series,
      };

      // **关键修复：调试信息**
      console.log("最终图表配置:");
      console.log("- Y轴范围: 0 -", maxYValue);
      console.log("- 满负荷收入:", revenueValues[10]);
      console.log("- 固定成本:", fixedCost);

      // 渲染图表
      try {
        this.breakevenChart.setOption(option, true);

        // 清理旧的resize监听器
        if (this._resizeHandler) {
          window.removeEventListener("resize", this._resizeHandler);
        }

        // 添加新的resize监听器
        this._resizeHandler = () => {
          if (this.breakevenChart) {
            this.breakevenChart.resize();
          }
        };
        window.addEventListener("resize", this._resizeHandler);

        console.log("图表绘制完成");
      } catch (error) {
        console.error("图表渲染失败:", error);
      }
    },
  },
};
</script>

<style scoped>
.breakeven-analysis-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

.table-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 150px;
  max-height: calc(100vh - 600px);
  margin-bottom: 20px;
}

.table-scroll-area {
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
  height: auto;
  min-height: 150px;
  max-height: calc(100vh - 600px);
}

.table-scroll-area::-webkit-scrollbar {
  width: 0;
  height: 6px;
}

.table-scroll-area::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.table-scroll-area::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.table-scroll-area::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

.breakeven-table {
  table-layout: auto;
  border-collapse: collapse;
  font-size: 12px;
  min-width: 100%;
  white-space: nowrap;
}

.breakeven-table th,
.breakeven-table td {
  text-align: center;
  border: 0.5px solid #dfe6ec;
  padding: 5px 3px;
  white-space: nowrap;
  font-size: 12px;
  height: 20px;
  line-height: 20px;
  box-sizing: border-box;
}

.breakeven-table thead th {
  background: #f8f9fa;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 10;
}

.seq-col {
  width: 180px;
  min-width: 180px;
  text-align: left !important;
  font-weight: 600;
}

.year-col {
  min-width: 50px;
  font-weight: 600;
}

.unit-col {
  min-width: 50px;
  color: #666;
  font-size: 11px;
}

.year-data-col {
  min-width: 50px;
  text-align: right !important;
}

.item-col {
  text-align: left !important;
  padding-left: 10px;
}

.indicators-section {
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 200px;
  position: absolute;
  bottom: 20px;
  left: 20px;
  z-index: 10;
}

.indicator-item {
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 12px;
  text-align: center;
  border: 1px solid #e4e7ed;
  box-sizing: border-box;
}

.indicator-label {
  font-size: 12px;
  color: #606266;
  margin-bottom: 5px;
  display: block;
}

.indicator-unit {
  font-size: 11px;
  color: #909399;
  margin-bottom: 5px;
  display: block;
}

.indicator-value {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  display: block;
}

.chart-section {
  position: absolute;
  bottom: 20px;
  left: 240px;
  right: 20px;
  height: 400px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
  box-sizing: border-box;
  display: flex;
  gap: 10px;
}

.chart-container {
  height: 100%;
  width: 400px;
  background-color: white;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  box-sizing: border-box;
}

.chart-legend-container {
  width: 200px;
  height: 100%;
  background-color: white;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  box-sizing: border-box;
  padding: 20px;
  overflow: auto;
}

.chart-legend-item {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding: 5px 0;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: 10px;
  display: inline-block;
}

.legend-text {
  font-size: 12px;
  color: #303133;
}
</style>
